/**
 * BE-288 portal authorization model.
 *
 * The token / profile carries the user's scope: which branches they see, which
 * services and modules they were granted, and per-module CRUD permissions. This
 * file holds the *pure* normalization + query logic so both AuthService and
 * PermissionsService can share it without a DI cycle.
 *
 * Wire vocabulary (matches the BE-288 payload): module ids are kebab-case,
 * service ids and actions are lowercase. Core deliberately speaks this wire
 * format and does NOT depend on the FE ServiceName enum.
 */

export type PortalModuleId =
  | 'branches'
  | 'users'
  | 'finance'
  | 'sales'
  | 'inventory'
  | 'marketing'
  | 'dismissal-call'
  | 'attendance'
  | 'notifications';

export type PermAction = 'view' | 'create' | 'edit' | 'delete';

export const PORTAL_MODULE_IDS: PortalModuleId[] = [
  'branches',
  'users',
  'finance',
  'sales',
  'inventory',
  'marketing',
  'dismissal-call',
  'attendance',
  'notifications',
];

export const PERM_ACTIONS: PermAction[] = ['view', 'create', 'edit', 'delete'];

/** Normalized, ready-to-query view of a user's scope. */
export interface AccessProfile {
  /** true ⇒ WAKI admin / no restriction claims ⇒ sees everything. */
  unrestricted: boolean;
  branchScope: 'all' | 'specific';
  branchIds: string[];
  /** Businesses the user is scoped to. Empty ⇒ not business-restricted. */
  businessIds: string[];
  serviceIds: string[];
  /** moduleId → granted actions. */
  modulePermissions: Record<string, Set<PermAction>>;
  /**
   * moduleId → set of granted page beIds (BE `portalAccess.pages` values, e.g.
   * `GATES`, `STATEMENT`). A module absent from this map carries NO page-level
   * restriction ⇒ all of its pages are visible (module-level grant / older
   * tokens). A module present here is restricted to exactly its listed pages.
   */
  pagesByModule: Record<string, Set<string>>;
  /**
   * FE-295: Inventory > Catalogue tabs (product type keys) the user may see, from
   * `portalAccess.subPages.INVENTORY.CATALOGUE`. `null` ⇒ the key is absent ⇒
   * every tab. Unlike pages, an empty set is a real grant: no tabs at all.
   */
  catalogueTabs: Set<string> | null;
  /** Only module is dismissal-call ⇒ fullscreen call-screen flow, no shell. */
  callScreenOnly: boolean;
}

/** Roles that always see the full portal, regardless of any scope claims. */
const WAKI_ADMIN_ROLES = new Set(['SUPER_ADMIN', 'WAKI_SUPER_ADMIN', 'ADMIN', 'admin']);

/** Legacy call-screen role, pre-dates BE-288 module_permissions. */
const LEGACY_DISMISSAL_TEACHER_ROLE = 'DISMISSAL_TEACHER';

function readRoles(user: any): string[] {
  const roles = user?.roles;
  return Array.isArray(roles) ? roles.map((r: any) => (typeof r === 'string' ? r : r?.name)).filter(Boolean) : [];
}

function isWakiAdmin(user: any): boolean {
  return readRoles(user).some(r => WAKI_ADMIN_ROLES.has(r));
}

/** Read branch ids across the shapes BE might send (claims or profile relations). */
function readBranchIds(user: any): string[] {
  const raw = user?.branchIds ?? user?.branch_ids ?? user?.branches;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((b: any) => (typeof b === 'string' ? b : b?.branch?.id ?? b?.branchId ?? b?.id))
    .filter(Boolean);
}

/** Read business ids across the shapes BE might send (claims or profile relations). */
function readBusinessIds(user: any): string[] {
  const raw = user?.businessIds ?? user?.business_ids ?? user?.businesses;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((b: any) => (typeof b === 'string' ? b : b?.business?.id ?? b?.businessId ?? b?.id))
    .filter(Boolean);
}

function readServiceIds(user: any): string[] {
  const raw = user?.serviceIds ?? user?.service_ids ?? user?.services ?? user?.serviceNames;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s: any) => (typeof s === 'string' ? s : s?.serviceId ?? s?.id ?? s?.name))
    .filter(Boolean)
    .map((s: string) => s.toLowerCase());
}

/**
 * Accepts either the token's object form `{ finance: ['view','edit'] }` or the
 * create-payload array form `[{ moduleId, view, create, edit, delete }]`.
 */
function readModulePermissions(user: any): Record<string, Set<PermAction>> {
  const raw = user?.modulePermissions ?? user?.module_permissions;
  const out: Record<string, Set<PermAction>> = {};
  if (!raw) return out;

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const moduleId = entry?.moduleId ?? entry?.module_id ?? entry?.module;
      if (!moduleId) continue;
      const set = new Set<PermAction>();
      for (const action of PERM_ACTIONS) {
        if (entry?.[action]) set.add(action);
      }
      if (set.size) out[String(moduleId).toLowerCase()] = set;
    }
    return out;
  }

  if (typeof raw === 'object') {
    for (const [moduleId, actions] of Object.entries(raw)) {
      if (!Array.isArray(actions)) continue;
      const set = new Set<PermAction>(
        (actions as string[]).map(a => a.toLowerCase()).filter((a): a is PermAction => (PERM_ACTIONS as string[]).includes(a)),
      );
      if (set.size) out[moduleId.toLowerCase()] = set;
    }
  }
  return out;
}

/**
 * BE-288 runtime claim: the JWT carries `portalAccess: { modules: ['FINANCE',
 * 'DISMISSAL_CALL', …], pages: { … } }`, where module keys are the UPPERCASE
 * PORTAL_MODULES catalog keys. Map each to its FE module id (lowercase kebab;
 * `DISMISSAL_CALL` → `dismissal-call`). portalAccess encodes *visibility* only —
 * there are no per-action grants — so a granted module is treated as fully
 * actionable; the BE does not restrict actions today. Tighten to real action
 * grants once the BE ships them.
 */
function readPortalAccessModules(user: any): Record<string, Set<PermAction>> {
  const modules = user?.portalAccess?.modules;
  const out: Record<string, Set<PermAction>> = {};
  if (!Array.isArray(modules)) return out;
  for (const key of modules) {
    if (!key) continue;
    const id = String(key).toLowerCase().replace(/_/g, '-');
    out[id] = new Set<PermAction>(PERM_ACTIONS);
  }
  return out;
}

/**
 * BE-288 runtime claim: `portalAccess.pages` is `{ BRANCHES: ['BRANCHES_LIST',
 * 'GATES'], FINANCE: ['STATEMENT'], … }` — per module, the page constants the
 * user may see. Normalize the UPPERCASE module keys to FE module ids (lowercase
 * kebab) and keep the page constants verbatim; the layer that owns the page
 * catalog (the users feature model) maps constants → sidebar routes. A module
 * that lists pages here is restricted to exactly those; a module absent here is
 * not page-restricted (empty lists are treated the same, so nothing is hidden
 * by an accidentally-empty claim).
 */
function readPortalAccessPages(user: any): Record<string, Set<string>> {
  const pages = user?.portalAccess?.pages;
  const out: Record<string, Set<string>> = {};
  if (!pages || typeof pages !== 'object') return out;
  for (const [key, list] of Object.entries(pages)) {
    if (!Array.isArray(list) || !list.length) continue;
    const id = String(key).toLowerCase().replace(/_/g, '-');
    out[id] = new Set((list as any[]).map(v => String(v)).filter(Boolean));
  }
  return out;
}

/** FE-295: `portalAccess.subPages.INVENTORY.CATALOGUE`, or null when absent (⇒ all tabs). */
function readCatalogueTabs(user: any): Set<string> | null {
  const tabs = user?.portalAccess?.subPages?.INVENTORY?.CATALOGUE;
  return Array.isArray(tabs) ? new Set(tabs.map((t: any) => String(t))) : null;
}

function hasExplicitScope(user: any): boolean {
  const scope = user?.branchScope ?? user?.branch_scope;
  if (scope === 'specific') return true;
  const portalModules = user?.portalAccess?.modules;
  if (Array.isArray(portalModules) && portalModules.length > 0) return true;
  const perms = user?.modulePermissions ?? user?.module_permissions;
  if (Array.isArray(perms)) return perms.length > 0;
  if (perms && typeof perms === 'object') return Object.keys(perms).length > 0;
  return false;
}

/** Normalize a raw user/profile object into a queryable AccessProfile. */
export function buildAccessProfile(user: any | null | undefined): AccessProfile {
  // 1) WAKI admins always see everything.
  if (isWakiAdmin(user)) {
    return unrestrictedProfile();
  }

  const roles = readRoles(user);
  const legacyTeacher = roles.includes(LEGACY_DISMISSAL_TEACHER_ROLE);

  // 2) Explicit BE-288 claims ⇒ restricted per claims.
  if (hasExplicitScope(user)) {
    const scopeRaw = user?.branchScope ?? user?.branch_scope;
    const branchScope: 'all' | 'specific' = scopeRaw === 'all' ? 'all' : 'specific';
    // Merge the legacy modulePermissions shape (if any) with the BE-288 runtime
    // portalAccess.modules claim actually emitted in the token.
    const modulePermissions = {
      ...readModulePermissions(user),
      ...readPortalAccessModules(user),
    };
    return {
      unrestricted: false,
      branchScope,
      branchIds: readBranchIds(user),
      businessIds: readBusinessIds(user),
      serviceIds: readServiceIds(user),
      modulePermissions,
      pagesByModule: readPortalAccessPages(user),
      catalogueTabs: readCatalogueTabs(user),
      callScreenOnly: onlyModuleIs(modulePermissions, 'dismissal-call'),
    };
  }

  // 3) Legacy dismissal teacher (pre-claims) ⇒ call-screen-only.
  if (legacyTeacher) {
    return {
      unrestricted: false,
      branchScope: 'specific',
      branchIds: readBranchIds(user),
      businessIds: readBusinessIds(user),
      serviceIds: ['dismissal'],
      modulePermissions: { 'dismissal-call': new Set<PermAction>(['view']) },
      pagesByModule: {},
      catalogueTabs: null,
      callScreenOnly: true,
    };
  }

  // 4) No claims at all ⇒ unrestricted (preserves today's behavior).
  return unrestrictedProfile();
}

function unrestrictedProfile(): AccessProfile {
  return {
    unrestricted: true,
    branchScope: 'all',
    branchIds: [],
    businessIds: [],
    serviceIds: [],
    modulePermissions: {},
    pagesByModule: {},
    catalogueTabs: null,
    callScreenOnly: false,
  };
}

function onlyModuleIs(modulePermissions: Record<string, Set<PermAction>>, moduleId: PortalModuleId): boolean {
  const keys = Object.keys(modulePermissions);
  return keys.length === 1 && keys[0] === moduleId;
}

// ─── Pure queries over an AccessProfile ───

export function canDo(profile: AccessProfile, moduleId: PortalModuleId, action: PermAction): boolean {
  if (profile.unrestricted) return true;
  return profile.modulePermissions[moduleId]?.has(action) ?? false;
}

/** Module is visible/reachable ⇒ user has at least `view` on it. */
export function hasModule(profile: AccessProfile, moduleId: PortalModuleId): boolean {
  if (profile.unrestricted) return true;
  return canDo(profile, moduleId, 'view');
}

export function allowedModules(profile: AccessProfile): PortalModuleId[] {
  if (profile.unrestricted) return [...PORTAL_MODULE_IDS];
  return PORTAL_MODULE_IDS.filter(m => hasModule(profile, m));
}

/**
 * Page beIds the user may see under `moduleId`, or `null` when the module
 * carries no page-level restriction (⇒ show every page). Unrestricted users are
 * never page-restricted. Consumers translate the beIds to sidebar routes.
 */
export function grantedPageBeIds(profile: AccessProfile, moduleId: PortalModuleId | string): Set<string> | null {
  if (profile.unrestricted) return null;
  const set = profile.pagesByModule[moduleId];
  return set && set.size ? set : null;
}

/** FE-295: may the user see this Inventory > Catalogue tab (product type)? */
export function isCatalogueTabVisible(profile: AccessProfile, type: string): boolean {
  return profile.catalogueTabs === null || profile.catalogueTabs.has(type);
}

export function isAllBranches(profile: AccessProfile): boolean {
  return profile.unrestricted || profile.branchScope === 'all';
}

export function inBranchScope(profile: AccessProfile, branchId: string): boolean {
  return isAllBranches(profile) || profile.branchIds.includes(branchId);
}

/** No business restriction ⇒ admin, or a scoped user with no business claim. */
export function isAllBusinesses(profile: AccessProfile): boolean {
  return profile.unrestricted || profile.businessIds.length === 0;
}

export function allowedBusinessIds(profile: AccessProfile): string[] {
  return profile.businessIds;
}

/** Post-login landing: call-screen users go fullscreen, everyone else dashboard. */
export function landingRouteFor(profile: AccessProfile): string {
  return profile.callScreenOnly ? '/dismissal-call/filter' : '/dashboard';
}
